/**
 * Block Availability wire types — Flutter parity.
 *
 * Source: `scrips_shared_features/lib/features/scheduling_module/
 * data/datamodels/block_availability_model.dart`
 *
 * Endpoints (`scheduling_data_source_impl.dart:455-518`):
 *  - GET  /api/Appointment/BlockAppointment?id={id}  → BlockAvailabilityRecord
 *  - POST /api/Scheduling/BlockSlot                  → UpdateSlotStatus
 *  - POST /api/Scheduling/UnblockSlot                → Status
 *
 * Wire-shape note: the request body shipped to /BlockSlot is the
 * narrow `BlockSlotRequest` below, NOT the full record returned by
 * /BlockAppointment. Flutter's `BlockAvailability.toJson()` drops the
 * local-only fields (`note`, `blockDate`, `practitioner`, …) and only
 * emits 6 keys.
 *
 * Wire dates are `.NET`-compatible no-Z local-time ISO
 * (`YYYY-MM-DDTHH:mm:ss.sss`). Flutter builds `start`/`end` from
 * `blockDate` + `TimeData` via `DateTime(...)` which yields a LOCAL
 * DateTime — `toIso8601String()` on that does NOT append `Z`. Same
 * .NET model-binder rule as the rest of the scheduling API.
 *
 * Verification status (per CLAUDE.md conversion contract):
 *  - Wire types: AUTHORITATIVE from Dart source (this file).
 *  - Mutation hooks (`useSaveBlockSlot`, `useUnblockSlot`): NOT YET WRITTEN —
 *    require a working dev curl before shipping.
 */

/** Hour/minute/format tuple — Flutter `TimeData` (`block_availability_model.dart:126-148`). */
export interface BlockTimeData {
  /** 12h hour (1-12). */
  hour: number | null;
  /** 0-59. */
  minute: number | null;
  /** 'am' | 'pm' (lower-case in Flutter; case-insensitive on the wire). */
  format: string | null;
}

/**
 * Recurring period values — verbatim from Flutter `app_constants.dart:366-368`:
 *   `dayPeriod = "Days"`, `weekPeriod = "Weeks"`, `monthPeriod = "Months"`.
 * Backend matches against the exact title-cased plural strings; do NOT
 * lowercase or singularize.
 */
export const BLOCK_PERIOD_DAYS = 'Days';
export const BLOCK_PERIOD_WEEKS = 'Weeks';
export const BLOCK_PERIOD_MONTHS = 'Months';
export type BlockRecurringPeriod =
  | typeof BLOCK_PERIOD_DAYS
  | typeof BLOCK_PERIOD_WEEKS
  | typeof BLOCK_PERIOD_MONTHS;

/**
 * Weekday names — verbatim from Flutter `app_constants.dart:513-521`
 * `weekList`. Ship full-name strings (`"Monday"` etc), not 3-letter
 * abbreviations.
 */
export const BLOCK_WEEK_DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;
export type BlockWeekDay = (typeof BLOCK_WEEK_DAYS)[number];

/**
 * Recurring rule — Flutter `Recurring` (`block_availability_model.dart:150-190`).
 *
 * Wire composition (Flutter `block_availability_widget.dart:223-237`):
 *   - `startDate` = blockDate (year/month/day) + START time (hh/mm).
 *   - `endDate`   = user-picked end date (year/month/day) + END time (hh/mm).
 *   Both shipped as no-Z ISO local datetime.
 *
 * `days` is populated only when `period === "Weeks"`. Flutter
 * auto-includes the block's weekday in the list when period flips to
 * Weeks (`recurring_widget.dart:60-78`).
 */
export interface BlockRecurring {
  /** Server-side recurring-rule id; null on create, populated on edit. */
  id: string | null;
  /** No-Z ISO local datetime. */
  startDate: string | null;
  /** No-Z ISO local datetime. */
  endDate: string | null;
  /** Repeat interval — integer ≥ 1. */
  repeatEvery: number | null;
  /** "Days" | "Weeks" | "Months" — use BLOCK_PERIOD_* constants. */
  period: BlockRecurringPeriod | null;
  /** Full weekday names ("Monday" etc) from BLOCK_WEEK_DAYS. Empty
   *  list when period is Days/Months. */
  days: string[];
}

/**
 * Request body for `POST /api/Scheduling/BlockSlot`.
 *
 * Mirrors Flutter `BlockAvailability.toJson()` 1:1 — 6 keys exactly.
 * The `comment` wire field is populated from Flutter's LOCAL `note`
 * field (`block_availability_model.dart:102`) — i.e. the free-text
 * the user types in the "Note" textbox lands as `comment` on the wire,
 * NOT the Flutter `comment` local-only field which is unused.
 */
export interface BlockSlotRequest {
  /**
   * Provider being blocked. Flutter binds this to
   * `practitioner.practiceSetupId` (the practice-scoped practitioner
   * id). On the wire this is a GUID string.
   */
  actorId: string;
  /** Start datetime — no-Z ISO local. */
  start: string;
  /** End datetime — no-Z ISO local. */
  end: string;
  // Flutter: note (local field) → wire: comment ⚠️ don't rename
  /**
   * Free-text reason. Flutter sources this from the user's `note`
   * input, NOT the local `comment` field. Don't rename to `note` —
   * the wire key is `comment`. (Samer review §4 — inline comment
   * above this field so call-site greps catch it without paging
   * into the JSDoc.)
   */
  comment: string;
  /** Practice GUID — `selectedPractice.practiceSetupId` in Flutter. */
  practiceId: string;
  /** Recurring rule. `null` for single-occurrence blocks. */
  recurring: BlockRecurring | null;
}

/**
 * GET /api/Appointment/BlockAppointment?id={id} response.
 *
 * Mirrors Flutter `BlockAvailability.fromJson()` — note the wire uses
 * `startTime` / `endTime` for the time-of-day tuples and `startDate` /
 * `endDate` for full datetimes. `blockDate` is the day-only anchor.
 */
export interface BlockAvailabilityRecord {
  id: string | null;
  note: string | null;
  /** Local-only on Flutter; wire still emits both `comment` and `note`. */
  comment: string | null;
  organizationId: string | null;
  practitioner: BlockPractitioner | null;
  practiceId: string | null;
  /** Day anchor — no-Z ISO. */
  blockDate: string | null;
  /** Full start datetime when recurring — no-Z ISO. */
  startDate: string | null;
  /** Full end datetime when recurring — no-Z ISO. */
  endDate: string | null;
  /** Time-of-day tuple for the block's start (12h + am/pm). */
  startTime: BlockTimeData | null;
  /** Time-of-day tuple for the block's end (12h + am/pm). */
  endTime: BlockTimeData | null;
  recurring: BlockRecurring | null;
}

/**
 * One participant on a device block record — the same three fields a device
 * tile already arrives with everywhere else on this tab.
 *
 * `referenceType` is `"Device"`, `reference` the device GUID, `display` its
 * registry name. Identical to what `POST /api/Appointment/ResourceList`
 * (PROD-2382) puts on a blocked device slot and what the `BlockResourceSlot`
 * SignalR broadcast (PROD-2510) carries, deliberately: one device block is one
 * object to this client however it arrives.
 */
export interface BlockDeviceParticipant {
  /** `"Device"` on every participant this route emits. */
  referenceType: string | null;
  /** Device GUID — the block's `actorId`. */
  reference: string | null;
  /** The device's registry name. Null when the machine has been archived out. */
  display: string | null;
}

/**
 * GET /api/Appointment/BlockResourceAppointment?id={id} response — the DEVICE
 * block-hydration route (PROD-2512, Scheduling `5acfb488`).
 *
 * It EXTENDS the provider record rather than restating it, because the server
 * type does exactly the same thing: `BlockResourceAppointmentResponse` derives
 * from `BlockAppointmentRequest`, so every field the edit form already reads is
 * the same field in the same place, produced by the same engine.
 *
 * The one addition is `participant`, and it exists because the inherited
 * `practitioner` slot cannot hold a device: it is typed `ProviderResponse`
 * server-side, and resolving a device id through the practitioner registry is
 * precisely the defect PROD-2512 closed (a device id matches no practitioner,
 * PracticeManagement returned null from a unary gRPC handler, null could not be
 * serialised, and reopening a device block answered a bare 500). `practitioner`
 * is therefore always null on this route.
 */
export interface BlockResourceAvailabilityRecord extends BlockAvailabilityRecord {
  /** Exactly one `"Device"` participant. Null only on a malformed response. */
  participant: BlockDeviceParticipant[] | null;
}

/**
 * Practitioner sub-shape on a block record. Reads the fields the
 * BlockAvailability panel actually displays + the ID it ships as
 * `actorId` on save.
 *
 * Flutter's `BlockAvailability.actorId` is initialised from the
 * practice's `practiceSetupId` then overwritten with the doctor's
 * `.id` on provider select (`block_availability_widget.dart:74, 437`).
 * The wire shape is just the provider's GUID — no separate
 * `practiceSetupId`. Verified against the dev curl
 * 2026-05-18 (`actorId: "0350fbef-..."` matched
 * `ProviderSlotsResponse[i].id`).
 *
 * `name` is the full display string (e.g. "Dr Jane Smith") returned by
 * `POST /api/Doctor/ProviderSlots`. `firstName` / `lastName` are
 * optional fallbacks; the response doesn't always populate them.
 */
export interface BlockPractitioner {
  /** Provider GUID — ships as `actorId` on the BlockSlot request. */
  id: string;
  /** Full display name from the API (`ProviderDetailResponse.name`). */
  name: string;
  /** Photo URL from the API (`ProviderDetailResponse.photo`). */
  photo?: string | null;
  /** Speciality display string (e.g. "Cardiology") — purely for UI. */
  speciality?: string | null;
}

/**
 * WHICH KIND of actor the Block Availability panel is blocking (PROD-2454).
 *
 * `POST /api/Scheduling/BlockSlot` has always taken `actorId`, not
 * `practitionerId` — the wire was actor-generic from day one and only the UI
 * was practitioner-only. This discriminant is the UI catching up: it decides
 * which picker the panel renders and which feed the break form's shift list
 * comes from, and nothing else. It never travels on the wire, because the
 * server does not need telling — a device actor id resolves to a device
 * schedule.
 */
export const BLOCK_ACTOR_PRACTITIONER = 'practitioner';
export const BLOCK_ACTOR_DEVICE = 'device';
export type BlockActorKind =
  | typeof BLOCK_ACTOR_PRACTITIONER
  | typeof BLOCK_ACTOR_DEVICE;

/**
 * The selected actor held by the panel's form state — a practitioner OR a
 * device, projected to the handful of fields the panel displays plus the id
 * it ships as `actorId`.
 *
 * ONE slot, not two. The panel can never have both a practitioner and a
 * device selected, and modelling that as two nullable fields would make the
 * invariant something to remember rather than something the type enforces —
 * the same reasoning that collapsed `showCreate`/`showBlock` into the single
 * `activePanel` enum in `frame-scheduling`. The KIND lives on the form state
 * beside this, not inside it, because it is known BEFORE an actor is picked:
 * it is what decides which picker to draw in the first place.
 *
 * `subtitle` is the picker card's secondary line — a practitioner's
 * speciality, a device's technician line. Display only; it never ships.
 */
export interface BlockActor {
  /** Practitioner or device GUID — ships as `actorId` on the BlockSlot request. */
  id: string;
  /** Display name — the provider's name, or the device's registry name. */
  name: string;
  /** Avatar URL. Devices have none; the picker card falls back to initials. */
  photo?: string | null;
  /** Secondary display line — speciality (practitioner) / technician (device). */
  subtitle?: string | null;
}

/**
 * Request body for `POST /api/Scheduling/UnblockSlot`.
 *
 * Flutter `scheduling_data_source_impl.dart:499-503`:
 *   { id, practiceId, recurringId }
 *
 * - `id`: block id (always required).
 * - `practiceId`: practice GUID.
 * - `recurringId`: when present, unblock the recurring series;
 *   when null, unblock just this single occurrence.
 */
export interface UnblockSlotRequest {
  id: string;
  practiceId: string;
  recurringId: string | null;
}
